import {
  customerBookOrderHistoryV1Schema,
  customerBookProductFactsV1Schema,
  customerDeliveryDraftV1Schema,
  customerFulfillmentStatusSchemaV1,
  customerOrderNotificationHistoryV1Schema,
  customerOriginalOrderInitiationReceiptV1Schema,
  physicalOrderReviewSchemaV1,
  physicalOrderSchemaV1,
  redactedDeliveryPlanSchemaV1,
  type CustomerBookOrderHistoryV1,
  type CustomerBookProductFactsV1,
  type CustomerDeliveryDraftV1,
  type CustomerFulfillmentStatusV1,
  type CustomerOrderNotificationHistoryV1,
  type RedactedDeliveryPlanV1,
  type SaveCustomerDeliveryDraftV1,
} from "@lifescapsule/contracts";
import { ApiError, apiRequest } from "../../shared/api";

/**
 * Printing was stopped, or closed to new orders, while this page was open (owner decision 6,
 * 2026-10-15). The customer's address was never the problem, so it isn't blamed.
 */
export function printingPaused(error: unknown) {
  return (
    error instanceof ApiError &&
    (error.code === "FULFILLMENT_EMERGENCY_STOP" ||
      error.code === "FULFILLMENT_RUNTIME_GATE_DISABLED")
  );
}

/**
 * The order's own checks refused it when placed (Stage 2, A1): over the copy's cost caps, a
 * printing limit reached, delivery changed, or something the copy needs no longer holding. The
 * service words these for customers; nothing was sent to print.
 */
const PRINT_ORDER_REFUSALS = new Set([
  "PRINT_ORDER_OVER_COST_LIMIT",
  "PRINT_ORDER_LIMIT_REACHED",
  "PRINT_ORDER_DELIVERY_CHANGED",
  "PRINT_ORDER_NEEDS_REVIEW",
]);
export function printOrderRefusal(error: unknown): string | null {
  return error instanceof ApiError && PRINT_ORDER_REFUSALS.has(error.code ?? "")
    ? error.message
    : null;
}

export const PRINTING_PAUSED_MESSAGE =
  "Ordering printed copies is paused right now, so nothing was sent to print. Your book and its included printed copy are saved. Please try again later.";

export type DeliveryAddressInput = {
  recipientName: string;
  line1: string;
  line2?: string;
  city: string;
  region: string;
  postalCode: string;
  country: "US";
  phone?: string;
};

export async function initiatePhysicalOrder(input: {
  idToken: string;
  bookId: string;
  previewId: string;
  expectedRevision: number;
  entitlementId: string;
  replacementOfPhysicalOrderId?: string;
  address: DeliveryAddressInput;
  idempotencyKey: string;
}) {
  const response = await apiRequest<{ data: unknown }>(
    `/books/${encodeURIComponent(input.bookId)}/physical-orders`,
    input.idToken,
    {
      method: "POST",
      headers: { "idempotency-key": input.idempotencyKey },
      body: JSON.stringify({
        previewId: input.previewId,
        expectedRevision: input.expectedRevision,
        entitlementId: input.entitlementId,
        confirmedExactApprovedVersion: true,
        ...(input.replacementOfPhysicalOrderId
          ? { replacementOfPhysicalOrderId: input.replacementOfPhysicalOrderId }
          : {}),
        address: input.address,
      }),
    },
  );
  const value = response.data as { physicalOrder?: unknown; review?: unknown };
  return {
    physicalOrder: physicalOrderSchemaV1.parse(value.physicalOrder),
    review: physicalOrderReviewSchemaV1.parse(value.review),
  };
}

/**
 * A book's printed-copy orders, newest first (engine G3). Only orders indexed since the history
 * was added are listed; an older one may be missing, so an empty list is not "never ordered".
 */
export async function listBookPhysicalOrders(
  idToken: string,
  bookId: string,
  options: { limit?: number; cursor?: string } = {},
): Promise<CustomerBookOrderHistoryV1> {
  const query = new URLSearchParams();
  if (options.limit) query.set("limit", String(options.limit));
  if (options.cursor) query.set("cursor", options.cursor);
  const response = await apiRequest<{ data: unknown }>(
    `/books/${encodeURIComponent(bookId)}/physical-orders${query.size ? `?${query}` : ""}`,
    idToken,
  );
  return customerBookOrderHistoryV1Schema.parse(response.data);
}

export async function getPhysicalOrderStatus(
  idToken: string,
  physicalOrderId: string,
): Promise<CustomerFulfillmentStatusV1> {
  const response = await apiRequest<{ data: unknown }>(
    `/physical-orders/${encodeURIComponent(physicalOrderId)}`,
    idToken,
  );
  return customerFulfillmentStatusSchemaV1.parse(response.data);
}

export async function confirmPhysicalOrderAddress(
  idToken: string,
  physicalOrderId: string,
  address: DeliveryAddressInput,
  idempotencyKey: string,
): Promise<RedactedDeliveryPlanV1> {
  const response = await apiRequest<{ data: unknown }>(
    `/physical-orders/${encodeURIComponent(physicalOrderId)}/delivery-address`,
    idToken,
    {
      method: "PUT",
      headers: { "idempotency-key": idempotencyKey },
      body: JSON.stringify(address),
    },
  );
  return redactedDeliveryPlanSchemaV1.parse(response.data);
}

export async function requestPhysicalOrderCancellation(
  idToken: string,
  physicalOrderId: string,
  idempotencyKey: string,
): Promise<void> {
  await apiRequest(
    `/physical-orders/${encodeURIComponent(physicalOrderId)}/cancellation`,
    idToken,
    {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey },
      body: JSON.stringify({ reasonCode: "CUSTOMER_REQUEST" }),
    },
  );
}

export async function submitPhysicalOrder(
  idToken: string,
  physicalOrderId: string,
  idempotencyKey: string,
): Promise<void> {
  await apiRequest(`/physical-orders/${encodeURIComponent(physicalOrderId)}/submission`, idToken, {
    method: "POST",
    headers: { "idempotency-key": idempotencyKey },
    body: JSON.stringify({ confirmedSendToPrint: true }),
  });
}

export async function getPhysicalOrderTrackingUrl(
  idToken: string,
  physicalOrderId: string,
  shipmentId: string,
): Promise<string> {
  const response = await apiRequest<{ data?: { url?: unknown } }>(
    `/physical-orders/${encodeURIComponent(physicalOrderId)}/shipments/${encodeURIComponent(shipmentId)}/tracking`,
    idToken,
  );
  if (typeof response.data?.url !== "string" || new URL(response.data.url).protocol !== "https:")
    throw new Error("Tracking is unavailable.");
  return response.data.url;
}

/**
 * The customer's private delivery draft for a book (engine G2), or null when there is none or it
 * has expired. A draft is convenience state only: it is not a confirmed address, not an order and
 * never reaches the printer.
 */
export async function getDeliveryDraft(
  idToken: string,
  bookId: string,
): Promise<CustomerDeliveryDraftV1 | null> {
  const response = await apiRequest<{ data?: unknown }>(
    `/books/${encodeURIComponent(bookId)}/delivery-draft`,
    idToken,
  );
  if (response.data === null) return null;
  const draft = customerDeliveryDraftV1Schema.parse(response.data);
  if (draft.bookId !== bookId) throw new Error("The saved draft belongs to another book.");
  return draft;
}

/**
 * Saves the draft only if it is still at `expectedVersion` (null: no draft yet). Another tab or
 * device saving first answers 409 (`draftConflict`); nothing is overwritten.
 */
export async function saveDeliveryDraft(
  idToken: string,
  bookId: string,
  input: SaveCustomerDeliveryDraftV1,
): Promise<CustomerDeliveryDraftV1> {
  const response = await apiRequest<{ data?: unknown }>(
    `/books/${encodeURIComponent(bookId)}/delivery-draft`,
    idToken,
    { method: "PUT", body: JSON.stringify(input) },
  );
  const draft = customerDeliveryDraftV1Schema.parse(response.data);
  if (draft.bookId !== bookId) throw new Error("The saved draft belongs to another book.");
  return draft;
}

/** The draft changed since it was read: a newer one is saved. */
export function draftConflict(error: unknown) {
  return error instanceof ApiError && error.status === 409;
}

/**
 * Whether an order was recorded for the original initiation key. NOT_OBSERVED is only that: it
 * doesn't say the printer refused anything, nor that sending again is allowed.
 */
export async function getOriginalOrderInitiation(
  idToken: string,
  bookId: string,
  requestKey: string,
) {
  const response = await apiRequest<{ data?: unknown }>(
    `/books/${encodeURIComponent(bookId)}/physical-order-initiation?${new URLSearchParams({ requestKey })}`,
    idToken,
  );
  const receipt = customerOriginalOrderInitiationReceiptV1Schema.parse(response.data);
  if (receipt.bookId !== bookId) throw new Error("The receipt belongs to another book.");
  if (receipt.receipt === "RECORDED" && !receipt.physicalOrderId)
    throw new Error("The receipt names no order.");
  return receipt;
}

export type OriginalOperation = {
  operation: "ADDRESS" | "SUBMISSION" | "CANCELLATION";
  physicalOrderId: string;
  originalRequestKey: string;
  /** UNKNOWN: still unresolved, never resend; REJECTED_NOT_DISPATCHED: refused before dispatch. */
  state: "UNKNOWN" | "COMPLETED" | "REJECTED_NOT_DISPATCHED";
  updatedAt: string;
};

const operationStates = new Set(["UNKNOWN", "COMPLETED", "REJECTED_NOT_DISPATCHED"]);

/**
 * The order's latest original request of one kind, as the service journaled it, or null when none
 * was journaled. It names the key it was sent with, so a page can tell its own request from
 * another tab's.
 */
export async function getOriginalOperation(
  idToken: string,
  physicalOrderId: string,
  operation: OriginalOperation["operation"],
): Promise<OriginalOperation | null> {
  const response = await apiRequest<{ data?: unknown }>(
    `/physical-orders/${encodeURIComponent(physicalOrderId)}/operations/${operation}`,
    idToken,
  );
  if (response.data === null) return null;
  const value = response.data as Partial<Record<keyof OriginalOperation, unknown>> | undefined;
  if (
    !value ||
    value.operation !== operation ||
    value.physicalOrderId !== physicalOrderId ||
    typeof value.originalRequestKey !== "string" ||
    typeof value.state !== "string" ||
    !operationStates.has(value.state) ||
    typeof value.updatedAt !== "string"
  )
    throw new Error("The original request record is unreadable.");
  return value as OriginalOperation;
}

/**
 * The emails sent about an order and what the email service reported for each (engine G5).
 * Acceptance by the email service is not delivery, and nothing here ever says a message was read.
 */
export async function getOrderNotifications(
  idToken: string,
  physicalOrderId: string,
  cursor?: string,
): Promise<CustomerOrderNotificationHistoryV1> {
  const response = await apiRequest<{ data?: unknown }>(
    `/physical-orders/${encodeURIComponent(physicalOrderId)}/notifications${cursor ? `?${new URLSearchParams({ cursor })}` : ""}`,
    idToken,
  );
  const history = customerOrderNotificationHistoryV1Schema.parse(response.data);
  if (history.physicalOrderId !== physicalOrderId)
    throw new Error("The email history belongs to another order.");
  return history;
}

/**
 * The book's product facts (engine E4.3): what was measured from the exact approved PDF, kept
 * apart from the printed product's own status, the included copy and extra copies. Facts grant
 * nothing: no approval, print permission, price or checkout.
 */
export async function getBookProductFacts(
  idToken: string,
  bookId: string,
): Promise<CustomerBookProductFactsV1> {
  const response = await apiRequest<{ data?: unknown }>(
    `/books/${encodeURIComponent(bookId)}/product-facts`,
    idToken,
  );
  const facts = customerBookProductFactsV1Schema.parse(response.data);
  if (facts.identity.bookId !== bookId) throw new Error("The facts belong to another book.");
  return facts;
}
